<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
defineOptions({ inheritAttrs: false });
import { cssSize } from '@/presentation/utils/ui';
import { registerDialog, unregisterDialog } from '@/presentation/composables/useDialogStack';
const props = withDefaults(defineProps<{ modelValue: boolean; maxWidth?: string | number; persistent?: boolean }>(), { maxWidth: 540 });
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>();
const dialog = ref<HTMLDialogElement>();
let previousFocus: HTMLElement | null = null;
function synchronize(): void {
  const open = props.modelValue;
  const element = dialog.value;
  if (!element) return;
  if (open && !element.open) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    element.showModal();
    registerDialog(element);
  } else if (!open && element.open) {
    unregisterDialog(element);
    element.close();
    if (previousFocus?.isConnected) previousFocus.focus();
  }
}
watch(() => props.modelValue, synchronize, { flush: 'post' });
onMounted(synchronize);
function cancel(event: Event): void {
  event.preventDefault();
  if (!props.persistent) emit('update:modelValue', false);
}
function backdrop(event: MouseEvent): void {
  if (event.target !== dialog.value) return;
  const rect = dialog.value.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) cancel(event);
}
function onClose(): void {
  const element = dialog.value;
  if (!element) return;
  unregisterDialog(element);
  if (props.modelValue && !element.open) emit('update:modelValue', false);
}
onBeforeUnmount(() => {
  if (dialog.value) unregisterDialog(dialog.value);
  if (dialog.value?.open) dialog.value.close();
  if (previousFocus?.isConnected) previousFocus.focus();
});
</script>
<template>
  <Teleport to="body">
    <dialog
      v-bind="$attrs"
      ref="dialog"
      class="ava-dialog"
      :style="{ maxWidth: cssSize(maxWidth) }"
      aria-modal="true"
      :aria-label="$attrs['aria-labelledby'] ? undefined : typeof $attrs['aria-label'] === 'string' ? $attrs['aria-label'] : 'Janela de diálogo'"
      @cancel="cancel"
      @click="backdrop"
      @close="onClose"
    >
      <slot v-if="modelValue" />
    </dialog>
  </Teleport>
</template>
